/**
 * 체크 칸 드래그 선택 (2026-08-21 · 2026-08-23 재설계).
 *
 * 규칙:
 *  · 시작은 **체크 칸에서만** — 줄 아무 데서나 끌리면 글자를 긁을 수 없고 메일을 열려던 손이 샌다.
 *  · 시작한 줄의 **반대**로 칠한다 — 한 동작에 칠하기와 지우기가 섞이지 않는다.
 *  · ★ 터치는 **암묵 캡처** 탓에 지나가는 줄에 pointerenter 가 오지 않는다 →
 *    `elementFromPoint` 로 손끝 아래의 줄을 직접 집는다.
 *  · ★ 끌기 끝의 click 을 **삼킨다** — 안 삼키면 방금 칠한 줄이 곧바로 뒤집힌다.
 *
 * ★★ 2026-08-23 — **지나간 줄을 기억하는 방식에서 "시작~지금 사이" 방식으로 바꿨다.**
 *
 *   예전에는 지나친 줄을 그 자리에서 칠하고 끝이라, 손을 **되돌려 올라와도 칠한 것이 그대로**
 *   남았다. 사람은 "너무 많이 잡았으니 도로 올라가면 풀리겠지" 라고 하는데 안 풀렸고, 그것이
 *   "선택이 안 풀린다" 로 보였다. 게다가 끌기 전에 손으로 골라 둔 것이 지나가는 길에 있으면
 *   **말없이 지워졌다** — "선택되어 있는 게 선택 해제된다".
 *
 *   이제 끌기가 시작될 때 **그때의 선택을 통째로 기억**해 두고, 움직일 때마다
 *   `기억한 것 + (시작~지금 사이만 칠하기)` 를 새로 만든다. 그래서
 *     · 되돌아 올라가면 범위에서 빠진 줄이 **원래대로 돌아온다**(취소가 된다).
 *     · 범위 밖의 줄은 **한 번도 건드리지 않는다** — 미리 골라 둔 것이 안 사라진다.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface DragSelectApi {
  /** 체크 칸의 onPointerDown 에 그대로 연결한다. */
  onCheckPointerDown: (e: React.PointerEvent, id: string) => void;
  /** 끌고 있는 동안 true — 줄에 커서·선택 금지를 걸 때 쓴다. */
  dragging: boolean;
  /** 끌기 직후의 click 인지 — 소비처가 click 을 무시할지 판단한다. */
  shouldSwallowClick: () => boolean;
}

/** 줄에 붙어 있어야 하는 표식 — 이 속성으로 좌표에서 줄을 찾는다. */
export const DRAG_ROW_ATTR = 'data-mail-id';

/** 화면에 그려진 순서대로 줄 id 를 읽는다. 묶어 보기에서도 눈에 보이는 순서 그대로다. */
function rowIdsInOrder(): string[] {
  return [...document.querySelectorAll(`[${DRAG_ROW_ATTR}]`)]
    .map((el) => el.getAttribute(DRAG_ROW_ATTR))
    .filter((v): v is string => v !== null);
}

export function useDragSelect(
  setPicked: (updater: (prev: Set<string>) => Set<string>) => void,
): DragSelectApi {
  const [dragging, setDragging] = useState(false);
  /** 칠할까(true) 지울까(false) — 시작한 줄의 반대. */
  const paintRef = useRef(true);
  /** 끌기가 시작될 때의 선택 — 범위 밖은 여기서 그대로 가져온다. */
  const baseRef = useRef<Set<string>>(new Set());
  const anchorRef = useRef<string | null>(null);
  const swallowRef = useRef(false);
  const activeRef = useRef(false);

  /** 시작~지금 사이를 칠한 결과로 통째로 바꾼다. 범위 밖은 시작할 때 그대로다. */
  const applyRange = useCallback((toId: string) => {
    const anchor = anchorRef.current;
    if (anchor === null) return;
    setPicked((prev) => {
      const order = rowIdsInOrder();
      const a = order.indexOf(anchor);
      const b = order.indexOf(toId);
      // 줄이 화면에서 사라졌으면(쪽 넘김 등) 손대지 않는다 — 엉뚱한 범위를 칠하느니 아무것도 안 한다.
      if (a < 0 || b < 0) return prev;
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      const next = new Set(baseRef.current);
      for (let i = lo; i <= hi; i += 1) {
        const id = order[i]!;
        if (paintRef.current) next.add(id); else next.delete(id);
      }
      // 바뀐 게 없으면 그대로 — 헛렌더를 만들지 않는다.
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
      return next;
    });
  }, [setPicked]);

  const onCheckPointerDown = useCallback((e: React.PointerEvent, id: string) => {
    // 오른쪽·가운데 버튼으로는 칠하지 않는다.
    // ★ `!== 0` 이 아니라 `> 0` 으로 본다 — jsdom 은 PointerEvent 를 구현하지 않아
    //   button 이 비어 오는데, `!== 0` 으로 막으면 시험에서 아무 일도 일어나지 않는다.
    if (e.button > 0) return;
    e.preventDefault();
    const row = (e.currentTarget as HTMLElement).closest(`[${DRAG_ROW_ATTR}]`);
    const startOn = row?.getAttribute('data-picked') === 'true';
    paintRef.current = !startOn;
    anchorRef.current = id;
    activeRef.current = true;
    setDragging(true);
    // ★ 지금 선택을 그대로 읽어 둔다 — 값을 바꾸지 않는 갱신이라 다시 그리지 않는다.
    setPicked((prev) => { baseRef.current = new Set(prev); return prev; });
    applyRange(id);
  }, [applyRange, setPicked]);

  useEffect(() => {
    if (!dragging) return;

    const move = (ev: PointerEvent) => {
      if (!activeRef.current) return;
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const row = el?.closest(`[${DRAG_ROW_ATTR}]`) as HTMLElement | null;
      const id = row?.getAttribute(DRAG_ROW_ATTR);
      // 줄 밖(머리줄·여백)으로 나가면 **마지막 범위를 그대로 둔다** — 손이 잠깐 벗어났다고
      // 칠한 것이 사라지면 끌기가 덜덜거린다.
      if (id) applyRange(id);
    };
    const up = () => {
      activeRef.current = false;
      anchorRef.current = null;
      setDragging(false);
      swallowRef.current = true;
      // 같은 tick 의 click 만 삼킨다 — 다음 클릭까지 막으면 줄을 못 연다.
      setTimeout(() => { swallowRef.current = false; }, 0);
    };

    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', up);
    // ★ 창 밖에서 손을 떼면 pointerup 이 안 온다 — 그대로 두면 다음에 마우스만 움직여도
    //   칠해진다. 창이 초점을 잃으면 끌기를 끝낸 것으로 본다.
    window.addEventListener('blur', up);
    return () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
      window.removeEventListener('blur', up);
    };
  }, [dragging, applyRange]);

  const shouldSwallowClick = useCallback(() => swallowRef.current, []);

  return { onCheckPointerDown, dragging, shouldSwallowClick };
}
